/**
 * OutreachTab — v2 sourcing tab shell. Follows the shape of the roster tabs
 * beside it (Scripts, Confirmed): campaignId from useCampaignUiStore,
 * EmptyState for the zero/error/disabled cases, a header row, and the
 * roster tabs' own paginator (DataTablePagination) rather than a new one.
 *
 * Row-open now routes to ThreadView (the entry's message history, rendered
 * in-page via the `?entry=` search param — see useOutreachSubParam) in place
 * of the header/filters/table. SendHoldBanner sits at the top of EVERY
 * sub-view — the hold applies to the whole deployment, so it must not be
 * something you only see on the Inbox.
 *
 * Earlier tasks: the import drawer (paste handles → preview → commit) wired to
 * both the empty state's CTA and the header button shown once the tab already
 * has rows; Find contact (useFindContactStatus, which also tracks each row's
 * Looking…/Search failed phase) and contact editing
 * (useOutreachContact, behind a single shared Dialog housing
 * ContactEditPopover — opened from ContactCell's own inline "Add an email"
 * affordance on a no-contact row, funneling through handleEditContact/
 * editingEntryId state) plus useOutreachSSE for live row updates.
 *
 * Task 22: the row menu's "View profile" opens CreatorProfileDrawer via
 * profileEntryId; row actions go through useOutreachEntryActions; Suppress
 * calls the admin suppressions routes directly (ruling 52); Delete and bulk
 * Remove share handleRemove(entryIds).
 *
 * Task 23: Move to brand dashboard, Delete, bulk Remove and Suppress confirm
 * through one ActionConfirmDialog (`pending`, discriminated by kind); bulk
 * Remove's `remove_entries` operation renders its own OperationProgress.
 *
 * Task 24: a deep-linked entry not on the current page is fetched directly
 * (useOutreachRow, ruling 60) instead of showing an explanatory empty state.
 * ThreadView takes the entry-action callbacks so its "⋯" menu reuses them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useCampaignUiStore,
  useOutreachRows,
  useOutreachCounts,
  useOutreachContact,
  useOutreachSettings,
  useOutreachBulkSends,
  fetchOutreachBulkSend,
  useFindContactStatus,
  useOutreachSSE,
  useOutreachEntryActions,
  useOutreachRow,
  createImportBatchApi,
  useCampaignHeaderQuery,
  campaignKeys,
  useSendNowOutreachMessage,
} from '@discovr/features';
import { isNotFoundError, useCan } from '@discovr/common';
import { SCOPES, isOutreachWireError, isWithBrandStatus } from '@discovr/types';
import type {
  OutreachCloseReason,
  OutreachContactPatch,
  OutreachInboxTab,
  OutreachQuote,
  OutreachRow,
} from '@discovr/types';
import { Envelope } from '../../../design-system/icons';
import { cn } from '../../../lib/utils';
import { EmptyState } from '../../../components/common/EmptyState';
import { DataTablePagination } from '../../../components/common/DataTable';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { OUTREACH_MAX_PAGE_SIZE, PAGE_SIZE_OPTIONS, useCampaignPageSizeParam } from '../../useCampaignPageSizeParam';
import { formatRelative } from '../../utils/campaignDetailHelpers';
import { OutreachRowsTable } from './OutreachRowsTable';
import { outreachStatusLabel } from './OutreachStatusPill';
import { OutreachFilters } from './OutreachFilters';
import { activeCount } from './FilterPanel';
import { InboxTabBar } from './InboxTabBar';
import { OutreachHeaderActions } from './OutreachHeaderActions';
import { OutreachPullDialog } from './OutreachPullDialog';
import { usePublishHeaderActions } from '../../../components/common/useHeaderActionsSlot';
import { CreatorImportWizard } from '../CreatorsTab/CreatorImportWizard';
import { ContactEditPopover } from './ContactEditPopover';
import { ProfileSheet } from './ProfileSheet';
import { RowStopDialog, type RowStopTarget } from './RowStopDialog';
import { AgreeRateDialog } from './AgreeRateDialog';
import { useComposerSeed } from './useComposerSeed';
import { useStickyRow } from './useStickyRow';
import { TemplatesView } from './TemplatesView';
import { CrmView } from './CrmView';
import { AwaySendWatchers, type AwaySend } from './wizard/AwaySendWatchers';
import { BulkSendWizard } from './wizard/BulkSendWizard';
import type { WizardTarget } from './wizard/wizardState';
import { nothingSendableReason, partialSendNote, targetBlocker } from './wizard/wizardState';
import { firstTouchBlocker } from './RowActions';
import { SendHoldBanner } from './SendHoldBanner';
import { ThreadView } from './ThreadView';
import { useOutreachSubParam } from '../../useOutreachSubParam';
import { useCampaignPageParam } from '../../useCampaignPageParam';
import { useCampaignPillParam } from '../../useCampaignPillParam';
import { useOutreachFiltersParam } from './useOutreachFiltersParam';
import { InboxHeader } from './InboxHeader';
import { invalidFilterLabel } from './filterErrors';
import { INBOX_EMPTY_COPY, INBOX_EMPTY_ICON, INBOX_TAB_TO_API, inboxTabStage, parseInboxTab } from './inboxTabs';
import { findContactEntryIds } from './contactLookup';
import { emphasizeDiscovery, fmtAgreed } from './outreachTabText';
import { OutreachOperationPanels } from './OutreachOperationPanels';
import { OutreachConfirmDialog, type PendingConfirm } from './OutreachConfirmDialog';
import { useFindContactsFlow } from './useFindContactsFlow';
import { isInOutreach } from './handoffStates';
import { wireErrorCopy } from './wireErrorCopy';
import { useMovedIds } from './useMovedIds';
import { codeOf, detailsOf, entryActionErrorCopy, recoveryErrorCopy } from './sendLabels';
import { retryHeldBackNote, retryTarget } from './retryLabels';
import { useOutreachRetry } from './useOutreachRetry';
import { useSendWizardSession } from './useSendWizardSession';
import { useSuppressionMutations } from './useSuppressionMutations';
import { useCanWriteOutreach } from './useCanWriteOutreach';

/** Server cap on one send-to-brand batch (spec §4). */
const MAX_BULK_MOVE = 200;

const sameForDisplay = (remembered: OutreachRow | undefined, row: OutreachRow): boolean =>
  !!remembered &&
  remembered.username === row.username &&
  remembered.displayName === row.displayName &&
  remembered.avatarUrl === row.avatarUrl &&
  remembered.email === row.email &&
  remembered.emailOverride === row.emailOverride &&
  remembered.sendTz === row.sendTz &&
  remembered.contactLookupSupported === row.contactLookupSupported;

export function OutreachTab() {
  const { campaignId } = useCampaignUiStore();

  const { sub: subView, entry, setEntry, openRosterChat } = useOutreachSubParam();
  const ack = useComposerSeed(setEntry);
  const canWrite = useCanWriteOutreach();
  const hasManageScope = useCan(SCOPES.outreachManage);
  const canOverrideAddressGuard = canWrite && hasManageScope;

  const [pillParam, setActiveTab] = useCampaignPillParam('all');
  const activeTab = parseInboxTab(pillParam);
  const { query, setQuery, sort, setSort, filters, setFilters, unrecognisedStage } = useOutreachFiltersParam();
  const [page, setPage] = useCampaignPageParam();
  const [pageSize, setPageSize] = useCampaignPageSizeParam(OUTREACH_MAX_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [pullOpen, setPullOpen] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [stopTarget, setStopTarget] = useState<RowStopTarget | null>(null);
  const [profileEntryId, setProfileEntryId] = useState<string | null>(null);
  // Agree-rate dialog (Task 25) — opened from either ThreadHeader's "Agree
  // rate…" menu item or the profile drawer's rate section, both of which
  // only carry an entryId (+ the thread's own latest quote, when open from
  // inside one); the row itself is resolved below from whichever source has
  // it (the page, the deep-linked thread, or the standalone profile fetch).
  const [agreeFor, setAgreeFor] = useState<{ entryId: string; quote?: OutreachQuote; row?: OutreachRow } | null>(null);
  const send = useSendWizardSession();
  // Sends the admin closed the wizard on while they were still running: watched here so their end is still toasted.
  const [awaySends, setAwaySends] = useState<AwaySend[]>([]);
  // Every Bulk send not yet sent, from Go (any admin's, surviving a reload): the top bar lists them.
  const bulkSends = useOutreachBulkSends(campaignId);
  // The bulk-remove operation (Task 23) is its own durable batch, separate
  // from the send wizard's operationId/summary above.
  const [removeOperationId, setRemoveOperationId] = useState<string | null>(null);
  const [moveOperationId, setMoveOperationId] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  // Separate from `pending` so the dialog keeps its copy through the close
  // animation (clearing `pending` rendered "undefined moves to…", G1-b).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const openConfirm = (next: PendingConfirm) => {
    setPending(next);
    setConfirmOpen(true);
  };

  useOutreachSSE(campaignId);
  // The shared creator import, aimed at outreach: same flow as the Creators
  // tab's sheet, a handle and an optional email per row.
  const outreachImportApi = useMemo(() => createImportBatchApi('outreach'), []);
  const { data: campaignHeader } = useCampaignHeaderQuery(campaignId ?? '');
  const campaignHeaderLoaded = campaignHeader !== undefined;
  const queryClient = useQueryClient();
  const { findContact, phaseFor: findContactPhase, failureLabelFor: findContactFailure } = useFindContactStatus(campaignId);
  const findContacts = useFindContactsFlow(campaignId);
  const contactMutation = useOutreachContact(campaignId);
  const settingsQuery = useOutreachSettings(campaignId);
  const entryActions = useOutreachEntryActions(campaignId);
  const retry = useOutreachRetry(campaignId);
  // The row menu's single-message recovery (review CRITICAL-2): the
  // reason-aware single-row route, never the bulk resend_messages operation
  // retry.retry wraps — a held row needs sendNowHeldFix's precise sentence,
  // which only this route returns.
  const sendNow = useSendNowOutreachMessage(campaignId);
  const handleRowRetry = (entryId: string, messageId: number) => {
    sendNow.mutate(
      { entryId, messageId },
      { onError: (e) => toast.error(recoveryErrorCopy(e)) },
    );
  };
  // Shared with the CRM's creator list (outreach:manage on the BE).
  const { suppress: suppressMutation, unsuppress: unsuppressMutation } = useSuppressionMutations(campaignId);

  // setActiveTab/setQuery already reset page themselves (see
  // useCampaignPillParam/useOutreachFiltersParam) — a stale page number from
  // a longer previous result set must never survive a tab or search change.
  const handleTabChange = (tab: OutreachInboxTab) => {
    setActiveTab(tab);
    setSelectedIds([]);
    // "Scheduled time" only exists as a Sort-by option on the Scheduled tab
    // (OutreachFilters) — leaving it with that sort still selected would
    // both orphan the trigger's label (no ticked menu item matches it
    // anywhere else) and keep querying the rows API with a sort field that
    // doesn't apply off that tab.
    if (tab !== 'scheduled' && sort === 'scheduled_at') {
      setSort('last_activity');
    }
  };
  const handleQueryChange = (q: string) => {
    setQuery(q);
  };

  const countsQuery = useOutreachCounts(campaignId);
  const tabStage = inboxTabStage(activeTab, filters.stage);
  const rowsQuery = useOutreachRows(campaignId, {
    tab: INBOX_TAB_TO_API[activeTab],
    q: query || undefined,
    sort,
    page,
    limit: pageSize,
    reached_from: filters.reachedFrom,
    reached_to: filters.reachedTo,
    stage: tabStage ?? undefined,
    intent: filters.intent,
    locked_rate: filters.lockedRate,
  }, tabStage !== null);

  // A skipped request shares the unfiltered tab's cache key, and React Query serves cached data even to a disabled
  // query, so nothing read off it may reach the screen.
  const rowsSkipped = tabStage === null;
  const rowsData = rowsSkipped ? undefined : rowsQuery.data;
  const rowsError = rowsSkipped ? null : rowsQuery.error;
  const rows = rowsData?.rows ?? [];
  const total = rowsData?.total ?? 0;

  // The selection is ids only. A row on the page is authoritative — always, so a
  // find-contacts, an edit or a refetch is reflected at once. An id that paged away
  // keeps the last row we saw for display, but is unverified: the client cannot
  // vouch for it, so the server's gates (suppression, status, in-flight) decide.
  const pageRows = rowsData?.rows;
  const [lastSeen, setLastSeen] = useState<ReadonlyMap<string, OutreachRow>>(() => new Map());
  // Adjusted during render (not in an effect): only a row whose displayed fields differ from
  // the remembered one triggers an update, so fresh row objects with the same content settle.
  const newlySeen = (pageRows ?? []).filter((row) => !sameForDisplay(lastSeen.get(row.entryId), row));
  if (newlySeen.length > 0) {
    setLastSeen((previous) => new Map([...previous, ...newlySeen.map((row) => [row.entryId, row] as const)]));
  }

  const wizardTarget = (entryId: string): WizardTarget => {
    const live = rows.find((candidate) => candidate.entryId === entryId);
    const row = live ?? lastSeen.get(entryId);
    const email = row?.emailOverride ?? row?.email ?? undefined;
    return {
      entryId,
      username: row?.username ?? entryId,
      displayName: row?.displayName,
      avatarUrl: row?.avatarUrl,
      email,
      hasContact: !!email,
      sendTz: row?.sendTz ?? undefined,
      blocker: live ? firstTouchBlocker(live) : undefined,
      unverified: !live,
      lookupSupported: row?.contactLookupSupported,
    };
  };
  const handleToggleSelect = (entryId: string) => {
    setSelectedIds((previous) =>
      previous.includes(entryId) ? previous.filter((id) => id !== entryId) : [...previous, entryId],
    );
  };
  const handleToggleSelectAll = () => {
    const pageIds = rows.map((r) => r.entryId);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    setSelectedIds((previous) =>
      allSelected ? previous.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...previous, ...pageIds])),
    );
  };

  const handleOpenRow = (entryId: string) => {
    setEntry(entryId);
  };
  const handleFindContact = (entryId: string) => {
    findContact(entryId);
  };
  const handleEditContact = (entryId: string) => {
    setEditingEntryId(entryId);
  };
  const handleLeaveSending = (away: AwaySend) => setAwaySends((prev) => (prev.some((p) => p.operationId === away.operationId) ? prev : [...prev, away]));
  const handleViewProfile = (entryId: string) => {
    setProfileEntryId(entryId);
  };
  // The wizard's creators left the selection once a send went out, so the bar doesn't keep counting them (E9-100).
  const dropSentFromSelection = (sent: WizardTarget[] | null) => {
    if (!sent) return;
    const sentIds = new Set(sent.map((t) => t.entryId));
    setSelectedIds((previous) => previous.filter((id) => !sentIds.has(id)));
  };
  const handleClearSelection = () => {
    setSelectedIds([]);
  };
  // mutateAsync per call: a later mutate() replaces an earlier call's
  // callbacks (react-query v5), which would drop an overlapping error.
  const runEntryAction = async <T,>(call: () => Promise<T>, onSuccess?: (result: T) => void, errorCopy = entryActionErrorCopy) => {
    try {
      const result = await call();
      onSuccess?.(result);
    } catch (e) {
      toast.error(errorCopy(e));
    }
  };
  const handleMarkRead = (entryId: string) => void runEntryAction(() => entryActions.markRead.mutateAsync({ entryId }));
  const handleMarkUnread = (entryId: string) => void runEntryAction(() => entryActions.markUnread.mutateAsync({ entryId }));
  const rowById = (entryId: string) => rows.find((r) => r.entryId === entryId);
  const knownRow = (id: string) => [rowById(id), threadRow, profileRow].find((r) => r?.entryId === id) ?? undefined;
  const { markMoved, liveRemovable } = useMovedIds();
  const nameOf = (r?: OutreachRow) => r?.displayName || r?.username || 'this creator';
  const handlePauseFollowups = (entryId: string) =>
    void runEntryAction(() => entryActions.pauseFollowups.mutateAsync({ entryId }), () => toast.success(`Follow-ups paused for ${nameOf(rowById(entryId))}`));
  const handleResumeFollowups = (entryId: string) =>
    void runEntryAction(() => entryActions.resumeFollowups.mutateAsync({ entryId }), (r) => toast.success(r.scheduledAt ? `Next follow-up ${formatRelative(r.scheduledAt)}` : 'Follow-ups resumed'));
  // Thread ⋯ menu additions (Task 24) — same fire-and-toast pattern as
  // pause/resume above, not routed through the shared ActionConfirmDialog
  // (that's reserved for Move to brand dashboard/Delete/Suppress, Task 23).
  const handleClose = (entryId: string, reason: OutreachCloseReason) =>
    void runEntryAction(() => entryActions.close.mutateAsync({ entryId, reason }), () => toast.success('Conversation closed'));
  const handleReopen = (entryId: string) =>
    void runEntryAction(() => entryActions.reopen.mutateAsync({ entryId }), () => toast.success('Conversation reopened'));
  const handleNotUnsubscribe = (entryId: string) => {
    const row = knownRow(entryId);
    if (row?.notUnsubscribe) openConfirm({ kind: 'notUnsubscribe', entryId, name: nameOf(row), mode: row.notUnsubscribe });
  };
  const handleClearEscalation = (entryId: string) =>
    void runEntryAction(
      () => entryActions.clearEscalation.mutateAsync({ entryId }),
      () => toast.success(`Escalation cleared for ${nameOf(rowById(entryId) ?? deepRowQuery.data ?? undefined)}`),
      (e) => (codeOf(e) === 'NOT_ESCALATED' ? 'Already cleared.' : entryActionErrorCopy(e)),
    );
  const handleRecordBrandCounter = (entryId: string, note?: string) =>
    void runEntryAction(
      () => entryActions.recordBrandCounter.mutateAsync({ entryId, note }),
      () => toast.success('Recorded — the row now shows "Brand asked for less"'),
    );
  const handleClearBrandCounter = (entryId: string) =>
    void runEntryAction(
      () => entryActions.clearBrandCounter.mutateAsync({ entryId }),
      () => toast.success('Brand request cleared'),
      (e) => (codeOf(e) === 'NO_BRAND_COUNTER' ? 'Already cleared.' : entryActionErrorCopy(e)),
    );
  const handleNoReplyNeeded = (entryId: string) =>
    void runEntryAction(
      () => entryActions.noReplyNeeded.mutateAsync({ entryId }),
      () => toast.success('Marked as needing no reply'),
      (e) => (codeOf(e) === 'NOT_AWAITING_REPLY' ? 'Already answered or dismissed.' : entryActionErrorCopy(e)),
    );
  const lapsedFollowupError = (e: unknown) => (codeOf(e) === 'NO_LAPSED_FOLLOWUP' ? 'This follow-up was already handled.' : entryActionErrorCopy(e));
  const handleSendLapsedFollowup = (entryId: string) =>
    void runEntryAction(() => entryActions.sendLapsedFollowup.mutateAsync({ entryId }), () => toast.success('Follow-up sent'), lapsedFollowupError);
  const handleSkipLapsedFollowup = (entryId: string) =>
    void runEntryAction(() => entryActions.skipLapsedFollowup.mutateAsync({ entryId }), () => toast.success('Follow-up skipped'), lapsedFollowupError);
  const handleAgreeRate = (entryId: string, quote?: OutreachQuote) => {
    setAgreeFor({ entryId, quote, row: rowById(entryId) ?? threadRow ?? profileRow ?? undefined });
  };
  // These confirm through ActionConfirmDialog below; each just stages into `pending`.
  const handleMoveToBrand = (entryIds: string[]) => {
    const [only] = entryIds;
    if (!only) return;
    const single = entryIds.length === 1 ? knownRow(only) : undefined;
    const flag = single?.escalatedAt ? { reason: single.escalationReason } : undefined;
    openConfirm({ kind: 'moveToBrand', entryIds, name: entryIds.length === 1 ? nameOf(single) : undefined, rate: fmtAgreed(single?.agreedDeliverables), flag });
  };
  const handleSendToBrand = (entryId: string) => handleMoveToBrand([entryId]);
  const rowAddress = (entryId: string) => {
    const target = rows.find((r) => r.entryId === entryId);
    return target?.emailOverride ?? target?.email;
  };
  const handleDiscardDraft = (entryId: string, messageId: number) =>
    openConfirm({ kind: 'discardDraft', entryId, messageId, name: nameOf(knownRow(entryId)) });
  const handleSuppress = (entryId: string) => {
    const email = rowAddress(entryId);
    if (email) openConfirm({ kind: 'suppress', entryId, email });
  };
  const handleUnsuppress = (entryId: string) => {
    const suppressionId = rowById(entryId)?.suppressionId;
    if (suppressionId !== undefined) unsuppressMutation.mutate(suppressionId);
  };
  // One id or the whole selection alike (RowActions' single-row Delete and
  // BulkToolbar's Remove both funnel through this) — never a hard delete on
  // the BE (ruling 49).
  // A with-brand row is the brand's roster entry now — never soft-deleted from here.
  // Acts only on ids whose LIVE status is known right now (never a selection-time
  // snapshot, which goes stale); everything else is dropped and counted.
  const handleRemove = (requestedIds: string[]) => {
    const entryIds = liveRemovable(requestedIds, knownRow);
    const [only] = entryIds;
    if (!only) {
      toast.info('Nothing to remove on this page');
      return;
    }
    openConfirm({
      kind: 'remove',
      entryIds,
      name: entryIds.length === 1 ? nameOf(knownRow(only)) : undefined,
      skipped: requestedIds.length - entryIds.length,
    });
  };
  const confirmPending = async (reason?: string) => {
    if (!confirmOpen || !pending) return;
    // Re-checked here: rows may have refetched between opening the dialog and confirming.
    if (pending.kind === 'discardDraft') {
      const { entryId, messageId } = pending;
      setConfirmOpen(false);
      return void runEntryAction(
        () => entryActions.discardDraft.mutateAsync({ entryId, messageId }),
        () => toast.success('Draft discarded'),
      );
    }
    if (pending.kind === 'notUnsubscribe') {
      const { entryId, mode } = pending;
      setConfirmOpen(false);
      return void runEntryAction(
        () => entryActions.notUnsubscribe.mutateAsync({ entryId }),
        () => {
          if (mode === 'reopen') {
            toast.success('Conversation reopened');
            ack.navigate(entryId);
          } else toast.success('Block lifted and Needs you cleared');
        },
        (e) => (codeOf(e) === 'ENTRY_NOT_UNSUBSCRIBED' ? 'This is no longer held by an unsubscribe.' : entryActionErrorCopy(e)),
      );
    }
    const staged = pending.kind === 'remove' ? { ...pending, entryIds: liveRemovable(pending.entryIds, knownRow) } : pending;
    setConfirmOpen(false);
    if (staged.kind === 'remove') {
      const dropped = pending.kind === 'remove' ? pending.entryIds.length - staged.entryIds.length : 0;
      if (staged.entryIds.length === 0) return void toast.info('Nothing to remove on this page');
      if (dropped > 0) toast.info(`${dropped} selected ${dropped === 1 ? 'creator was' : 'creators were'} no longer removable and skipped`);
    }
    const [onlyEntryId] = staged.kind === 'remove' ? staged.entryIds : [];
    if (staged.kind === 'remove' && staged.entryIds.length === 1 && onlyEntryId) {
      entryActions.remove.mutate(
        { entryId: onlyEntryId, reason },
        {
          onSuccess: () => setSelectedIds((previous) => previous.filter((id) => id !== onlyEntryId)),
          onError: (e: unknown) => toast.error(entryActionErrorCopy(e)),
        },
      );
    } else if (staged.kind === 'remove') {
      try {
        const { operationId: newOperationId } = await entryActions.removeMany.mutateAsync({ entryIds: staged.entryIds, reason });
        setRemoveOperationId(newOperationId);
        setSelectedIds((previous) => previous.filter((id) => !staged.entryIds.includes(id)));
      } catch (e) {
        toast.error(entryActionErrorCopy(e));
      }
    } else if (staged.kind === 'suppress') {
      suppressMutation.mutate(staged.email, { onSuccess: () => toast.success(`Suppressed ${staged.email}`) });
    } else if (staged.entryIds.length > 1) {
      try {
        const { operationId: id } = await entryActions.sendToBrandMany.mutateAsync({ entryIds: staged.entryIds });
        setMoveOperationId(id);
        markMoved(staged.entryIds);
        setSelectedIds((previous) => previous.filter((selected) => !staged.entryIds.includes(selected)));
      } catch (e) {
        toast.error(entryActionErrorCopy(e));
      }
    } else {
      const [entryId] = staged.entryIds;
      if (!entryId) return;
      entryActions.sendToBrand.mutate(
        { entryId, ...(staged.flag ? { confirmOpenFlag: true } : {}) },
        {
          onSuccess: () => {
            toast.success(`${staged.name} moved to the brand dashboard`);
            markMoved([entryId]);
            setSelectedIds((previous) => previous.filter((id) => id !== entryId));
          },
          onError: (e: unknown) => {
            // The row was stale: Go still sees an open flag, so ask again with its reason.
            if (codeOf(e) === 'OPEN_FLAG_CONFIRM') {
              const reason = detailsOf(e)?.reason;
              return openConfirm({ ...staged, flag: { reason: typeof reason === 'string' ? reason : undefined } });
            }
            toast.error(entryActionErrorCopy(e));
          },
        },
      );
    }
  };
  // A selection survives paging, but `rows` only ever holds the CURRENT page —
  // resolving ids through it would silently drop every off-page selection and
  // send fewer creators than the bulk bar counted. The ids are carried through
  // verbatim; the row lookup is display enrichment only, and may miss.
  // `rows` is [] until the page's query resolves (every page/tab/filter change),
  // and then every id would read as unverified and lose its client-side blockers.
  // A skipped request never resolves, but there is nothing to load.
  const rowsReady = rowsData !== undefined || rowsSkipped;
  // The import hand-off has already closed its wizard, so its ids must never be dropped: they
  // are fresh imports (unverified by definition) and the server decides each one.
  const openFirstTouchWizard = (entryIds: string[]) => {
    if (entryIds.length === 0) return;
    send.open(entryIds.map(wizardTarget));
  };
  const handleSendFirstTouch = (entryIds: string[]) => {
    if (entryIds.length === 0) return;
    if (!rowsReady) {
      toast.info('Creators are still loading — try again in a moment.');
      return;
    }
    send.open(entryIds.map(wizardTarget));
  };
  const selectedList = selectedIds.map(wizardTarget);
  const sendableCount = selectedList.filter((t) => targetBlocker(t) === null).length;
  const sendDisabledReason = rowsReady ? nothingSendableReason(selectedList) : 'Loading creators…';
  const sendNote = rowsReady ? partialSendNote(selectedList) : undefined;
  const findContactIds = findContactEntryIds(selectedList);

  // Only rows on the current page are in memory, so an off-page selection is not retried; the bar's count says so.
  const selectedRows = selectedIds.flatMap((id) => {
    const r = rowById(id);
    return r ? [r] : [];
  });
  const retryMessageIds = selectedRows.flatMap((r) => {
    const target = retryTarget(r, { bulk: true });
    return target ? [target.messageId] : [];
  });
  // The owner's ruling: state the bulk/single-row split before the click.
  const retryNote = retryHeldBackNote(selectedRows);

  // The send hook already invalidates rows and counts on accept; this covers
  // the batch, whose rows only settle once the operation finishes.
  const handleQueued = () => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
  };

  const editingRow = rows.find((r) => r.entryId === editingEntryId) ?? null;
  const profileRow = rows.find((r) => r.entryId === profileEntryId) ?? null;
  // Ruling 60: a deep-linked entry not on the current table page is fetched
  // directly off the 2.2 Detail route (useOutreachRow) instead of showing
  // "this thread isn't on the current page" — that empty state is deleted.
  const deepLinkEntry = entry && !rowById(entry) ? entry : null;
  const deepRowQuery = useOutreachRow(campaignId, deepLinkEntry);
  // A contract break (Go sent a status this build does not know) is deterministic, so it is logged with its row and
  // value instead of leaving only a generic load failure for support to guess at.
  useEffect(() => {
    for (const error of [rowsError, deepRowQuery.error, countsQuery.error]) {
      if (isOutreachWireError(error)) console.error('[outreach] wire contract break', error);
    }
  }, [rowsError, deepRowQuery.error, countsQuery.error]);
  const threadRow = useStickyRow(entry, entry ? (rowById(entry) ?? deepRowQuery.data ?? null) : null);
  const agreeRow = agreeFor ? (rows.find((r) => r.entryId === agreeFor.entryId) ?? threadRow ?? profileRow ?? agreeFor.row ?? null) : null;
  // Closes the dialog only once the PATCH actually succeeds — a rejection
  // propagates back to ContactEditPopover, which shows it inline and leaves
  // editingEntryId (and so the dialog) untouched.
  const handleSaveContact = async (patch: OutreachContactPatch) => {
    if (!editingEntryId) return;
    await contactMutation.mutateAsync({ entryId: editingEntryId, patch });
    setEditingEntryId(null);
  };

  // BE only registers the outreach routes when its config group is set, so
  // a fresh environment 404s on /counts rather than returning zeros.
  const outreachDisabled = isNotFoundError(countsQuery.error);
  const isInboxView = !subView || subView === 'inbox';
  // Only the inbox LIST gets these — same as the old InboxHeader, which
  // simply didn't render while a thread (`entry`) was open.
  const { show: showWizard, openBatch, currentBatchId } = send;
  const openBulkSend = async (batchId: string) => {
    if (currentBatchId === batchId) {
      showWizard();
      return;
    }
    try {
      const batch = await fetchOutreachBulkSend(campaignId, batchId);
      openBatch(
        (batch.entries ?? []).map((entry) => ({ entryId: entry.entryId, username: entry.username, displayName: entry.displayName, hasContact: true, unverified: true })),
        { id: batch.batchId, startedAt: Date.parse(batch.startedAt), floor: batch.draftFloor, templateId: batch.templateId ?? 0 },
      );
    } catch {
      toast.error('Could not open this Bulk send. It may have been sent already.');
      void queryClient.invalidateQueries({ queryKey: campaignKeys.outreachBulkSends(campaignId) });
    }
  };
  // The header element is memoised on stable inputs; the click reads the latest handler through a ref.
  const openBulkSendRef = useRef(openBulkSend);
  useEffect(() => {
    openBulkSendRef.current = openBulkSend;
  });
  const onOpenBulkSend = useCallback((batchId: string) => void openBulkSendRef.current(batchId), []);
  const headerActions = useMemo(
    () =>
      isInboxView && !entry && !outreachDisabled ? (
        <OutreachHeaderActions onImport={canWrite ? () => setImportWizardOpen(true) : undefined} onPull={canWrite ? () => setPullOpen(true) : undefined} importDisabled={!campaignHeaderLoaded} bulkSends={bulkSends} onOpenBulkSend={onOpenBulkSend} />
      ) : null,
    [isInboxView, entry, outreachDisabled, canWrite, campaignHeaderLoaded, setImportWizardOpen, setPullOpen, bulkSends, onOpenBulkSend],
  );
  usePublishHeaderActions(headerActions);

  if (outreachDisabled) {
    return (
      <EmptyState
        icon={Envelope}
        title="Outreach is not enabled"
        body="Outreach sourcing isn't turned on for this environment yet."
      />
    );
  }

  if (subView && subView !== 'inbox') {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        <SendHoldBanner />
        {subView === 'templates' ? (
          <TemplatesView campaignId={campaignId} canWrite={canWrite} />
        ) : (
          <CrmView campaignId={campaignId} />
        )}
      </div>
    );
  }

  if (rowsError) {
    const badFilter = invalidFilterLabel(rowsError);
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        <SendHoldBanner />
        {badFilter ? (
          <EmptyState
            icon={Envelope}
            title="This link has a filter we can't apply"
            body={`The ${badFilter} filter in this link isn't valid.`}
            action={{ label: 'Clear filters', onClick: () => setFilters({}) }}
          />
        ) : isOutreachWireError(rowsError) ? (
          <p role="alert" className="py-16 text-center text-sm text-rose-500">{wireErrorCopy(rowsError)}</p>
        ) : (
          <p className="py-16 text-center text-sm text-rose-500">Couldn't load outreach rows. Please refresh.</p>
        )}
      </div>
    );
  }

  // Figma gives every tab its own genuinely-empty copy (Flow 1) — not just
  // All. A search or an active FilterPanel filter narrowing a non-empty tab
  // to zero rows is a different case (falls through to OutreachRowsTable's
  // own "No match" empty state below), so both are excluded.
  const isGloballyEmpty = !rowsQuery.isLoading && rows.length === 0 && !query && activeCount(filters) === 0;

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', !entry && 'gap-5')}>
      {/* The full-bleed thread has no page gutter, so its banners get their own; on the inbox list this wrapper is layout-transparent. */}
      <div data-testid="outreach-banners" className={entry ? 'flex shrink-0 flex-col gap-2 px-6 has-[>:not(.sr-only)]:py-2' : 'contents'}>
      <SendHoldBanner />
      {/* Counts that Go sent in a shape this build does not know leave every tab pill blank while the table still lists
          rows, which reads as "still loading". Say so, and keep saying so if a refetch breaks previously good counts. */}
      {isOutreachWireError(countsQuery.error) && (
        <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-body-sm-regular text-rose-800">
          Tab counts are unavailable. {wireErrorCopy(countsQuery.error)}
        </p>
      )}
      {/* Always mounted so the message is announced when it appears; a region inserted with its text is often missed. */}
      <div aria-live="polite" aria-atomic="true" data-testid="stage-notice" className={unrecognisedStage.length > 0 ? 'rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-body-sm-regular text-amber-800' : 'sr-only'}>
        {unrecognisedStage.length > 0 &&
          `This link filtered by a stage this version doesn't know (${unrecognisedStage.join(', ')}), so it was ignored and the list is not filtered by it.`}
      </div>
      </div>
      {entry ? (
        // A removed creator keeps an openable, read-only thread: removal is one model now (outreach state `removed`).
        threadRow && !isInOutreach(threadRow) && !isWithBrandStatus(threadRow.status) && threadRow.outreachState !== 'removed' ? (
          <div className="p-4 md:p-8">
          <EmptyState
            icon={Envelope}
            title={`@${threadRow.username} is with the brand now`}
            body={`Outreach is finished for this creator (${outreachStatusLabel(threadRow)}). Their pipeline lives on the campaign's Creators tab.`}
            action={{ label: 'Back to inbox', onClick: () => setEntry(null) }}
          />
          </div>
        ) : threadRow ? (
          <ThreadView key={threadRow.entryId}
            campaignId={campaignId}
            row={threadRow}
            pageRows={rows}
            canWrite={canWrite}
            canOverrideAddressGuard={canOverrideAddressGuard}
            campaignZone={settingsQuery.data?.sendTz}
            autoFollowups={settingsQuery.data?.autoFollowups}
            onBack={ack.leave}
            onNavigate={ack.navigate}
            onSaveContact={(patch) => contactMutation.mutateAsync({ entryId: threadRow.entryId, patch })}
            onMarkRead={handleMarkRead}
            onMarkUnread={handleMarkUnread}
            onPauseFollowups={handlePauseFollowups}
            onResumeFollowups={handleResumeFollowups}
            onClose={handleClose}
            onReopen={handleReopen}
            onNotUnsubscribe={handleNotUnsubscribe}
            onSendToBrand={handleSendToBrand}
            onRemove={handleRemove}
            onAgreeRate={handleAgreeRate}
            onClearEscalation={handleClearEscalation}
            onRecordBrandCounter={handleRecordBrandCounter}
            onClearBrandCounter={handleClearBrandCounter}
            onNoReplyNeeded={handleNoReplyNeeded}
            onSendLapsedFollowup={handleSendLapsedFollowup}
            onRetryLookup={handleFindContact}
            onSkipLapsedFollowup={handleSkipLapsedFollowup}
            onOpenRosterChat={(r) => openRosterChat(r.username)}
            composerSeed={ack.seedFor(threadRow.entryId)}
            onSeedConsumed={ack.consumed}
          />
        ) : deepRowQuery.isLoading ? (
          <p className="px-4 py-16 text-center text-sm text-muted-foreground">Loading thread…</p>
        ) : deepRowQuery.isError ? (
          <div className="p-4 md:p-8">
          <EmptyState
            icon={Envelope}
            title="Couldn't load this thread"
            body={isOutreachWireError(deepRowQuery.error) ? wireErrorCopy(deepRowQuery.error) : "Something went wrong loading it. Go back to the inbox and try again."}
            action={{ label: 'Back to inbox', onClick: () => setEntry(null) }}
          />
          </div>
        ) : (
          <div className="p-4 md:p-8">
          <EmptyState
            icon={Envelope}
            title="Couldn't find this thread"
            body="It may have been removed. Go back to the inbox and try again."
            action={{ label: 'Back to inbox', onClick: () => setEntry(null) }}
          />
          </div>
        )
      ) : (
        <>
          <InboxHeader
            counts={countsQuery.data}
            controls={
              <OutreachFilters
                campaignId={campaignId}
                tab={INBOX_TAB_TO_API[activeTab]}
                inboxTab={activeTab}
                query={query}
                onQueryChange={handleQueryChange}
                sort={sort}
                onSortChange={setSort}
                filters={filters}
                onFiltersChange={setFilters}
              />
            }
          />
          <OutreachOperationPanels
            campaignId={campaignId}
            removeOperationId={removeOperationId}
            onRemoveDone={() => {
              setRemoveOperationId(null);
              handleQueued();
            }}
            onRemoveError={() => {
              setRemoveOperationId(null);
              toast.error("Couldn't read this batch's progress. Try again.");
            }}
            findContacts={findContacts}
            retry={retry}
            moveOperationId={moveOperationId}
            onMoveSettled={() => setMoveOperationId(null)}
          />
          <InboxTabBar activeTab={activeTab} counts={countsQuery.data} onTabChange={handleTabChange} />
          {isGloballyEmpty ? (
            <EmptyState
              icon={INBOX_EMPTY_ICON[activeTab]}
              title={INBOX_EMPTY_COPY[activeTab].title}
              // Figma never repeats the header's Pull creators/Import CSV as a
              // button inside the empty body — the "no creators yet" copy's
              // "Discovery" is styled inline instead (no handler).
              body={emphasizeDiscovery(INBOX_EMPTY_COPY[activeTab].body)}
            />
          ) : (
            <>
              <OutreachRowsTable
                rows={rows}
                loading={rowsQuery.isLoading}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                onOpenRow={handleOpenRow}
                onFindContact={handleFindContact}
                findContactPhase={findContactPhase}
                findContactFailure={findContactFailure}
                onEditContact={handleEditContact}
                onOpenWaitingDraft={handleViewProfile}
                onSendFirstTouch={handleSendFirstTouch}
                onViewProfile={handleViewProfile}
                onMarkRead={handleMarkRead}
                onMarkUnread={handleMarkUnread}
                onPauseFollowups={handlePauseFollowups}
                onResumeFollowups={handleResumeFollowups}
                onSendToBrand={handleSendToBrand}
                onSuppress={handleSuppress}
                onUnsuppress={handleUnsuppress}
                onRemove={handleRemove}
                onClearEscalation={handleClearEscalation}
                onReopen={handleReopen}
                onNotUnsubscribe={handleNotUnsubscribe}
                onClearSelection={handleClearSelection}
                findContactsCount={findContactIds.length}
                onFindContacts={() => void findContacts.start(findContactIds, selectedList.filter((t) => !t.unverified && t.hasContact).length)}
                findContactsBusy={findContacts.busy}
                onRetry={handleRowRetry}
                onDiscardDraft={handleDiscardDraft}
                onStop={(entryId, messageId) => {
                  const row = knownRow(entryId);
                  if (row) setStopTarget({ row, messageId });
                }}
                retryCount={retryMessageIds.length}
                onRetryMany={() => void retry.retry(retryMessageIds)}
                retryBusy={retry.busy || sendNow.isPending}
                retryNote={retryNote}
                moveToBrand={
                  canWrite && activeTab === 'waiting_on_brand'
                    ? {
                        count: selectedIds.length,
                        onMove: () => handleMoveToBrand(selectedIds),
                        disabledReason: selectedIds.length > MAX_BULK_MOVE ? `Select up to ${MAX_BULK_MOVE} creators at a time` : undefined,
                      }
                    : undefined
                }
                canWrite={canWrite}
                campaignZone={settingsQuery.data?.sendTz}
                autoFollowups={settingsQuery.data?.autoFollowups}
                totalCount={total}
                sendableCount={sendableCount}
                sendDisabledReason={sendDisabledReason}
                sendNote={sendNote}
                activeTab={activeTab}
                filtered={!!query || activeCount(filters) > 0}
                onClearFilters={() => {
                  setQuery('');
                  setFilters({});
                }}
              />
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <DataTablePagination
                  pagination={{
                    page,
                    pageSize,
                    total,
                    onPageChange: setPage,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                    offerAllSizes: true,
                    // setPageSize already resets page itself (useCampaignPageSizeParam).
                    onPageSizeChange: setPageSize,
                    noun: 'creators',
                    // Without this the "show all" option offers `total` even past
                    // 100 rows, and picking it snaps straight back once
                    // useCampaignPageSizeParam(OUTREACH_MAX_PAGE_SIZE) clamps the
                    // request server-side (E2E O1 follow-up).
                    maxPageSize: OUTREACH_MAX_PAGE_SIZE,
                  }}
                />
              </div>
            </>
          )}
        </>
      )}
      {/* "Import CSV" opens the shared creator import with the outreach target.
          Closing invalidates rows/counts the same way a completed bulk-send
          does (handleQueued) so newly-added rows show up without a manual
          refresh, whether the admin finished the import or backed out early. */}
      <OutreachPullDialog
        open={pullOpen}
        campaignId={campaignId}
        onClose={() => {
          setPullOpen(false);
          handleQueued();
        }}
      />
      <CreatorImportWizard
        open={importWizardOpen}
        campaignId={campaignId}
        api={outreachImportApi}
        campaignName={campaignHeader?.name}
        campaignPlatform={campaignHeader?.platform}
        onClose={() => {
          setImportWizardOpen(false);
          handleQueued();
        }}
        onSendFirstTouch={(entryIds) => {
          // Closes the wizard first (mirrors Done's own reset+close) so the
          // bulk-send dialog it opens isn't stacked behind the results panel.
          setImportWizardOpen(false);
          handleQueued();
          openFirstTouchWizard(entryIds);
        }}
      />
      {/* A wizard session is per-send, so it is remounted once its selection is
          closed. operationId/summary are lifted above the remount (see their
          declaration) so a still-running or just-completed batch is not lost
          if this remounts for some other reason first. */}
      {send.targets && (
        <BulkSendWizard
          open={!send.hidden}
          onHide={send.hide}
          campaignId={campaignId}
          targets={send.targets}
          operationId={send.operationId}
          summary={send.summary}
          approveOperationId={send.approveOperationId}
          onOperationIdChange={send.setOperationId}
          onApproveOperationIdChange={send.setApproveOperationId}
          onSummaryChange={send.setSummary}
          session={send.store}
          canOverrideAddressGuard={canOverrideAddressGuard}
          canWrite={canWrite}
          onClose={() => {
            if (send.approveOperationId) dropSentFromSelection(send.targets);
            send.close();
          }}
          onQueued={() => {
            handleQueued();
            dropSentFromSelection(send.targets);
          }}
          onLeaveSending={handleLeaveSending}
          onNavigate={(tab) => {
            send.close();
            setEntry(null);
            handleTabChange(tab);
          }}
        />
      )}
      <AwaySendWatchers
        campaignId={campaignId}
        sends={awaySends}
        onFinished={(operationId) => {
          setAwaySends((prev) => prev.filter((p) => p.operationId !== operationId));
          handleQueued();
        }}
      />
      {/* Contact-edit modal, opened from ContactCell's own "Add an email"
          affordance on a no-contact row — see this file's own header comment. */}
      <Dialog open={!!editingRow} onOpenChange={(next) => { if (!next) setEditingEntryId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit contact</DialogTitle>
            <DialogDescription>Change this creator's email or phone.</DialogDescription>
          </DialogHeader>
          {editingRow && (
            <ContactEditPopover row={editingRow} onSave={handleSaveContact} onClose={() => setEditingEntryId(null)} />
          )}
        </DialogContent>
      </Dialog>
      <RowStopDialog campaignId={campaignId} target={stopTarget} onClose={() => setStopTarget(null)} />
      <OutreachConfirmDialog
        open={confirmOpen}
        pending={pending}
        busy={entryActions.discardDraft.isPending || entryActions.remove.isPending || entryActions.removeMany.isPending || suppressMutation.isPending || entryActions.sendToBrand.isPending || entryActions.sendToBrandMany.isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={(reason) => void confirmPending(reason)}
      />
      <ProfileSheet
        row={profileRow}
        campaignZone={settingsQuery.data?.sendTz}
        autoFollowups={settingsQuery.data?.autoFollowups}
        canWrite={canWrite}
        onSave={(entryId, patch) => contactMutation.mutateAsync({ entryId, patch })}
        onClose={() => setProfileEntryId(null)}
        onAgreeRate={(entryId) => handleAgreeRate(entryId)}
        onSendToBrand={handleSendToBrand}
        onPauseFollowups={handlePauseFollowups}
        onResumeFollowups={handleResumeFollowups}
      />
      {/* Agree-rate dialog (Task 25) — opened from ThreadHeader's "Agree
          rate…" menu item or either profile drawer's rate section. */}
      {agreeRow && (
        <AgreeRateDialog
          open
          campaignId={campaignId}
          row={agreeRow}
          latestQuote={agreeFor?.quote}
          onClose={() => setAgreeFor(null)}
          onAcknowledge={ack.acknowledge}
          onAcknowledged={(entryId) => {
            // A thread that isn't open has no composer to clear; Go already ended its saved draft.
            if (threadRow?.entryId === entryId) ack.clear(entryId);
          }}
        />
      )}
    </div>
  );
}
